import React, { useState, useEffect, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';

// Global cache to remember which images have already been loaded in this session
const loadedImagesCache = new Set<string>();

interface LazyImageProps {
  src: string;
  alt: string;
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  placeholderHeight?: string | number;
}

/**
 * LazyImage component that loads images only when they enter the viewport
 * Uses Intersection Observer API for performance
 */
const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  width = '100%',
  height = 'auto',
  style,
  className,
  onLoad,
  onError,
  placeholderHeight = 300,
}) => {
  const isCached = loadedImagesCache.has(src);
  const [isLoaded, setIsLoaded] = useState(isCached);
  const [isInView, setIsInView] = useState(isCached);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If it's already cached or no Intersection Observer support, load immediately
    if (isCached || !('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '200px', // Start loading 200px before image enters viewport
        threshold: 0.01,
      }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
      observer.disconnect();
    };
  }, [src, isCached]);

  const handleLoad = () => {
    loadedImagesCache.add(src);
    setIsLoaded(true);
    if (onLoad) {
      onLoad();
    }
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setHasError(true);
    if (onError) {
      onError();
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: width,
        minHeight: isLoaded ? 'auto' : placeholderHeight,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Show skeleton while loading */}
      {!isLoaded && !hasError && (
        <Skeleton
          variant="rectangular"
          width={width}
          height={placeholderHeight}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      )}

      {/* Show error message if image failed to load */}
      {hasError && (
        <Box
          sx={{
            width: '100%',
            height: placeholderHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.disabledBackground',
            color: 'text.secondary',
          }}
        >
          Failed to load image
        </Box>
      )}

      {/* Only render img tag when in view */}
      {isInView && !hasError && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={className}
          style={{
            width: width,
            height: height,
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            ...style,
          }}
        />
      )}
    </Box>
  );
};

export default LazyImage;
